"use client";

import { getTranslation } from "@/i18n/get-translation";
import AppointmentCalendar from "./appointment-calendar/appointment-calendar";
import DoctorAppointmentCard from "./doctor-appointment-card";
import DoctorAppointmetDetails from "./doctor-appointment-details";
import DoctorAppointmetVerification from "./doctor-appointment-verification";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "next/navigation";
import {
  IAppointmentPayload,
  IAppointmentStoreResponse,
  IDoctorInfoResponse,
  IErrorResponse,
  IReason,
} from "@/types/agenda.type";
import { getDoctorInfo, sendAppointment } from "@/services/agenda.service";
import "./doctor-appointment.css";
import DoctorAppointmentApproved from "./doctor-appointment-approved";
import { useRouter } from "next/navigation";
import { useSnackbar } from "notistack";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Tab } from "@headlessui/react";
import AngleDown from "@/icons/angleDown.svg";
import MOMENTJS_DATE_FORMATES from "@/common/constants/momentjs-format";
import dayjs from "dayjs";
import DoctorAppointmentHeader from "./doctor-appointment-header";
import {FaCheck} from 'react-icons/fa6'
import GoogleMap from "@components/google-map/google-map";

interface IDoctorAppointment {
  locale: string;
}

export default function DoctorAppointment({ locale }: IDoctorAppointment) {
  const { enqueueSnackbar } = useSnackbar();
  const t = getTranslation(locale, "appointment");
  const tErrors = getTranslation(locale, "errors");
  const [appStep, setAppStep] = useState<number>(1);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);
  const [selectedDateTime, setSelectedDateTime] = useState<string>();
  const params = useParams<{ id: string; department: string }>();
  const [doctorInfo, setDoctorInfo] = useState<IDoctorInfoResponse>();
  const [selectedReason, setSelectedReason] = useState<IReason>();
  const [appointmentPayload, setAppointmentPayload] =
    useState<IAppointmentPayload>();
  const [confirmationCode, setConfirmationCode] = useState<string>();
  const [appointmentId, setAppointmentId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const bookingSubmittedRef = useRef(false);
  const router = useRouter();

  useEffect(() => {
    getDoctorInfo(params.id, params.department)
      .then((res: IDoctorInfoResponse) => {
        setDoctorInfo(res);
        const ilocale: string = locale?.split("-")[0] || "en";
        const appPayload: IAppointmentPayload = {
          ...appointmentPayload,
          doctor_id: res.docInf.id,
          clinic_id: res.docInf.clinic_id,
          locale: ilocale,
        };
        setAppointmentPayload(appPayload);
      })
      .catch(() => {
        router.push(`/${locale}/not-found`);
      });
  }, []);

  useEffect(() => {
    if (selectedReason?.id) {
      const appPayload: IAppointmentPayload = {
        ...appointmentPayload,
        reasons: [selectedReason.id],
      };
      setAppointmentPayload(appPayload);
    }
  }, [selectedReason]);

  useEffect(() => {
    if (selectedDateTime && selectedReason?.id) {
      const appPayload: IAppointmentPayload = {
        ...appointmentPayload,
        from: dayjs(selectedDateTime).format(
          MOMENTJS_DATE_FORMATES.toDateTimeWithss
        ),
        duration: selectedReason.duration,
      };
      setAppointmentPayload(appPayload);
    }
  }, [selectedReason, selectedDateTime]);

  useEffect(() => {
    // LUCY-3110: trigger on patient_data + slot + reasons instead of patient_id.
    const ready =
      !!appointmentPayload?.patient_data?.email &&
      !!appointmentPayload?.from &&
      !!appointmentPayload?.reasons?.length;

    if (ready && !bookingSubmittedRef.current) {
      bookingSubmittedRef.current = true;
      setIsLoading(true);
      sendAppointment(appointmentPayload)
        .then((res: IAppointmentStoreResponse | IErrorResponse) => {
          if ("id" in res && "confirmation_code" in res) {
            setConfirmationCode(res.confirmation_code);
            setAppointmentId(res.id);
            setAppStep(4);
          } else {
            bookingSubmittedRef.current = false;
            enqueueSnackbar(tErrors("went_wrong"), { variant: "error" });
          }
        })
        .catch((error: Error) => {
          bookingSubmittedRef.current = false;
          enqueueSnackbar(error.message, { variant: "error" });
        }).finally(()=>{
          setIsLoading(false);
        });
    }
  }, [appointmentPayload]);

  const handleChangeReason = useCallback(
    (value: IReason) => {
      if (value?.id) {
        setSelectedReason(value);
        setSelectedDateTime('');
        setAppStep(2);
      }
    },
    [selectedReason]
  );

  return (
    <div className="text-sm md:text-base">
      {/* This is new Design PR */}
      {doctorInfo ? (
        <div>
          <DoctorAppointmentHeader
            locale={locale}
            selectedReason={selectedReason}
            doctorInfo={doctorInfo?.docInf}
            selectedDateTime={selectedDateTime}
            setAppStep={setAppStep}
            appStep={appStep}
          />
          <div className="max-w-[1630px] mx-auto">
          <section className="relative text-center px-4 lg:px-20 pb-4 lg:pb-20">
            <div className="flex text-left max-md:flex-wrap">
              <div className="basis-auto grow order-2 lg:order-1">
                <DoctorAppointmentCard
                  locale={locale}
                  selectedReason={selectedReason}
                  doctorInfo={doctorInfo?.docInf}
                  selectedDateTime={selectedDateTime}
                  setAppStep={setAppStep}
                  appStep={appStep}
                />
              </div>
              <div className="flex-none w-full pl-2 mt-4 relative order-1 lg:order-2 mb-4 lg:mb-0 lg:w-[38rem] lg:pl-[1.25rem] xl:-mt-[5.5rem] z-10">
                <div className="border border-agenda-blue-200 rounded-2xl shadow-[0.625rem_0.625rem_1.0625rem_rgba(0,4,254,0.1)] bg-white sticky top-28 max-md:top-20">
                  <div className="bg-gradient-to-t from-[#e5f5ff] to-white px-3 lg:px-6 py-3 lg:py-4 rounded-tl-2xl rounded-tr-2xl">
                    <h3 className="text-agenda-purple-800 mb-1 text-base lg:text-xl font-bold">
                      {t("book_text")} {doctorInfo?.docInf?.name}
                    </h3>
                    <p className="opacity-70">{t("fill_info")}</p>
                  </div>
                  <div className="">
                    <ul className="w-full flex flex-col">
                      <li
                        className={`p-6 ${
                          appStep === 1 || selectedReason ? "block" : "hidden"
                        } ${isConfirmed && "opacity-60 pointer-events-none"}`}
                      >
                        <div className="flex relative" id="visit-reason">
                          <div className={`absolute -left-[2.375rem] md:-left-10 -top-[0.313rem] md:-top-[0.313rem] w-7 h-7 md:w-8 md:h-8 text-xs md:text-base flex-none rounded-full ${selectedReason ? 'bg-green-500' : 'bg-agenda-blue-600'}  text-white flex items-center justify-center`}>
                            {selectedReason ? <FaCheck /> : '1'}
                          </div>
                          <div className="flex-auto">
                            <div className="font-bold md:font-medium mb-2 text-sm md:text-base">
                              {t("visit_reason")}
                            </div>
                            <div>
                              <Listbox
                                value={selectedReason}
                                onChange={handleChangeReason}
                              >
                                <div className="relative text-agenda-purple-800">
                                  <ListboxButton className="flex items-center h-10 md:h-11 mb-1 relative w-full cursor-pointer rounded-full bg-white border border-agenda-grey-200 hover:border-agenda-blue-600 py-1 md:py-2 text-left px-4 focus:outline-none text-sm md:text-base">
                                    <div className="block truncate flex-auto overflow-hidden">
                                      {selectedReason ? (
                                        selectedReason.title
                                      ) : (
                                        <span className="text-agenda-grey-200">
                                          {t("select_visit_reason")}
                                        </span>
                                      )}
                                    </div>
                                    <AngleDown className="mt-1 ml-2 w-[0.875rem]" />
                                  </ListboxButton>
                                  <ListboxOptions className="absolute right-0 w-full py-1 bg-white shadow-[0.625rem_0.625rem_1.0625rem_rgba(0,4,254,0.1)] rounded-lg border border-agenda-blue-600 flex flex-col z-20">
                                    {doctorInfo?.reasons &&
                                      doctorInfo?.reasons.map(
                                        (reason: IReason) => (
                                          <ListboxOption
                                            key={reason.id}
                                            className={({ selected }) =>
                                              `relative cursor-pointer select-none py-2 px-4 hover:bg-gray-100 ${
                                                selected
                                                  ? "bg-gray-100 text-agenda-blue-600"
                                                  : ""
                                              }`
                                            }
                                            value={reason}
                                          >
                                            {({ selected }) => (
                                              <>
                                                <span
                                                  className={`block truncate ${
                                                    selected
                                                      ? "font-medium"
                                                      : "font-normal"
                                                  }`}
                                                >
                                                  {reason.title}
                                                </span>
                                                {selected ? (
                                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                                    {/* <CheckIcon className="h-5 w-5" aria-hidden="true" /> */}
                                                  </span>
                                                ) : null}
                                              </>
                                            )}
                                          </ListboxOption>
                                        )
                                      )}
                                  </ListboxOptions>
                                </div>
                              </Listbox>
                            </div>
                          </div>
                        </div>
                      </li>
                      <li
                        className={`border-t border-t-gray-200 p-6 ${
                          appStep === 2 || selectedDateTime ? "block" : "hidden"
                        } ${isConfirmed && "opacity-60 pointer-events-none"}`}
                      >
                        <div className="flex relative" id="pick-slot">
                          <div className={`absolute -left-[2.375rem] md:-left-10 -top-[0.313rem] md:-top-[0.313rem] w-7 h-7 md:w-8 md:h-8 text-xs md:text-base flex-none rounded-full ${selectedDateTime ? 'bg-green-500' : 'bg-agenda-blue-600'} text-white flex items-center justify-center`}>
                            {selectedDateTime ? <FaCheck /> : '2'}
                          </div>
                          <div className="flex-auto">
                            <div className="font-bold md:font-medium mb-2">
                              {t("pick_a_time_slot")}
                            </div>
                            <div>
                              <AppointmentCalendar
                                setIsLoading={setIsLoading}
                                isLoading={isLoading}
                                appointmentPayload={appointmentPayload}
                                setAppointmentPayload={setAppointmentPayload}
                                doctorId={doctorInfo?.docInf.id}
                                reasons={doctorInfo?.reasons}
                                setSelectedDateTime={setSelectedDateTime}
                                setSelectedReason={setSelectedReason}
                                selectedReason={selectedReason}
                                setAppStep={setAppStep}
                                locale={locale}
                                selectedDateTime={selectedDateTime}
                              />
                            </div>
                          </div>
                        </div>
                      </li>
                      <li
                        className={`border-t border-t-gray-200 p-6 ${
                          appStep === 3 || appointmentId ? "block" : "hidden"
                        } ${isConfirmed && "opacity-60 pointer-events-none"}`}
                      >
                        <div className="flex relative" id="patient-info">
                          <div className={`absolute -left-[2.375rem] md:-left-10 -top-[0.313rem] md:-top-[0.313rem] w-7 h-7 md:w-8 md:h-8 text-xs md:text-base flex-none rounded-full ${appointmentId ? 'bg-green-500' : 'bg-agenda-blue-600'} text-white flex items-center justify-center`}>
                            {appointmentId ? <FaCheck /> : '3'}
                          </div>
                          <div className="flex-auto">
                            <div className="font-bold md:font-medium mb-2">
                              {t("patient_info")}
                            </div>
                            <div>
                              <DoctorAppointmetDetails
                                setAppointmentPayload={setAppointmentPayload}
                                isLoading={isLoading}
                                locale={locale}
                              />
                            </div>
                          </div>
                        </div>
                      </li>
                      <li
                        className={`border-t border-t-gray-200 p-6 ${
                          appStep === 4 || isConfirmed ? "block" : "hidden"
                        } ${isConfirmed && "opacity-60 pointer-events-none"}`}
                      >
                        <div className="flex relative" id="verification">
                          <div className={`absolute -left-[2.375rem] md:-left-10 -top-[0.313rem] md:-top-[0.313rem] w-7 h-7 md:w-8 md:h-8 text-xs md:text-base flex-none rounded-full  ${isConfirmed ? 'bg-green-500' : 'bg-agenda-blue-600'} text-white flex items-center justify-center`}>
                            {isConfirmed ? <FaCheck /> : '4'}
                          </div>
                          <div className="flex-auto">
                            <div className="font-bold md:font-medium mb-2">
                              {t("verification")}
                            </div>
                            <div>
                              <DoctorAppointmetVerification
                                setIsLoading={setIsLoading}
                                isLoading={isLoading}
                                appointmentId={appointmentId}
                                confirmationCode={confirmationCode}
                                setAppStep={setAppStep}
                                locale={locale}
                                setIsConfirmed={setIsConfirmed}
                              />
                            </div>
                          </div>
                        </div>
                      </li>
                      <li
                        className={`border-t border-t-gray-200 p-6 ${
                          appStep === 5 ? "block" : "hidden"
                        }`}
                      >
                        <div className="flex relative" id="confirmation">
                          <div className={`absolute -left-9.5 md:-left-10 -top-[0.313rem] md:-top-[0.313rem] w-7 h-7 md:w-8 md:h-8 text-xs md:text-base flex-none rounded-full  ${isConfirmed ? 'bg-green-500' : 'bg-agenda-blue-600'} text-white flex items-center justify-center`}>
                            {isConfirmed ? <FaCheck /> : '5'}
                          </div>
                          <div className="flex-auto">
                            <div className="font-bold md:font-medium mb-2">
                              {t("confirmation")}
                            </div>
                            <div>
                              <DoctorAppointmentApproved
                                locale={locale}
                                selectedReason={selectedReason}
                                doctorInfo={doctorInfo}
                                selectedDateTime={selectedDateTime}
                              />
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
          </div>
        </div>
      ) : (
        <div className="fixed inset-0 flex items-center justify-center">
          <div className="loader"></div>
        </div>
      )}
    </div>
  );
}
